import { useState } from 'react';
import { Button } from '@/components/ui/button';

interface TestMessageModalProps {
  destination: string;
  channel: 'email' | 'sms' | 'telegram';
  message: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  note?: string;
}

export function TestMessageModal({
  destination,
  channel,
  message,
  onConfirm,
  onCancel,
  note,
}: TestMessageModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const channelLabel = channel === 'email' ? 'Email' : channel === 'sms' ? 'SMS' : 'Telegram';

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-semibold mb-4">Send test message</h2>
        <div className="space-y-3 text-sm">
          <div>
            <div className="font-medium text-gray-600">Channel</div>
            <div className="text-gray-900">{channelLabel}</div>
          </div>
          <div>
            <div className="font-medium text-gray-600">Destination</div>
            <div className="text-gray-900 font-mono break-all">{destination}</div>
          </div>
          <div>
            <div className="font-medium text-gray-600">Message</div>
            <div className="text-gray-900">{message}</div>
          </div>
          {note && (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-yellow-800">
              {note}
            </div>
          )}
        </div>

        {error && <div className="mt-4 text-sm text-red-600">{error}</div>}

        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send test'}
          </Button>
        </div>
      </div>
    </div>
  );
}
